import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { Event, EventType, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  AppRoute,
  AuthenticationFacade,
  ConfigSettingsService,
  DeviceOrigin,
  TitleMachineService,
} from '@ebeihilfe/core';
import { DeviceService } from '@ebeihilfe/shared';
import { LayoutFacade } from '@ebeihilfe/web-logic';
import { TranslateService } from '@ngx-translate/core';
import * as pdfjsLib from 'pdfjs-dist';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, throttleTime } from 'rxjs/operators';

import { AppVertiseSmallComponent } from './components/app-vertise-small/app-vertise-small.component';
import { AppvertiseComponent } from './components/appvertise/appvertise.component';
import { FooterComponent } from './components/footer';
import { HeaderComponent } from './components/header';

@Component({
  imports: [
    AppvertiseComponent,
    AppVertiseSmallComponent,
    AsyncPipe,
    FooterComponent,
    HeaderComponent,
    NgClass,
    RouterOutlet,
  ],
  selector: 'portal-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthenticationFacade);
  private readonly configService = inject(ConfigSettingsService);
  private readonly deviceService = inject(DeviceService);
  private readonly layoutFacade = inject(LayoutFacade);
  private readonly router = inject(Router);
  private readonly titleMachine = inject(TitleMachineService);
  private readonly translate = inject(TranslateService);

  private readonly destroyed$ = new Subject<boolean>();
  private readonly PDF_WORKER_SRC = '/assets/pdf/pdf.worker.min.mjs';

  public displayToSmall: Observable<boolean>;
  public displayWidth = new BehaviorSubject<number>(window.innerWidth);
  public hasBackgroundImage$: Observable<boolean>;
  public hideAppVertise: boolean;
  // 768px entspricht dem Breakpoint aus `@media (width ≤ 48em)`
  // in portal-toolbar.component.scss. Ab dieser Viewport-Breite
  // wird das Burgermenü angezeigt.
  public readonly minWidthForScreen = 768;
  public onlyWeb: boolean;
  public isNewEinreichungActive$: Observable<boolean>;

  constructor() {
    this.deviceService.device = DeviceOrigin.Portal;

    // Set the workerSrc property for ng2-pdf-viewer to load the PDF worker script from the specified path instead of cdn
    // https://github.com/VadimDez/ng2-pdf-viewer#set-custom-path-to-the-worker
    window['pdfWorkerSrc'] = this.PDF_WORKER_SRC;
    pdfjsLib.GlobalWorkerOptions.workerSrc = this.PDF_WORKER_SRC;
  }

  @HostListener('window:resize', ['$event.target']) public onResize(target: Window): void {
    this.displayWidth.next(target.innerWidth);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  public ngOnInit(): void {
    this.loading();
    this.assigningVars();
    this.isNewEinreichungActive$ = this.router.events.pipe(
      filter((event: Event) => event.type === EventType.NavigationEnd),
      map((event: NavigationEnd) => decodeURIComponent(event.url).includes(AppRoute.EinreichungNew)),
      takeUntil(this.destroyed$),
    );

    this.hasBackgroundImage$ = this.layoutFacade.selectBackground$();
  }

  private assigningVars(): void {
    combineLatest([
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects.includes('faq')),
      ),
      this.auth.authenticationChange$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([onFaqPage, isAuthed]) => (this.hideAppVertise = onFaqPage || isAuthed));

    this.displayToSmall = this.displayWidth.asObservable().pipe(
      throttleTime(500),
      map((value) => value < this.minWidthForScreen),
    );

    this.onlyWeb = this.configService.appSettings.onlyWeb;
  }

  private loading(): void {
    this.titleMachine.run();
  }
}
